import ControlHitArea from 'components/inputs/control-hit-area';
import { AccessoryProductType } from 'data/types';
import { ReactNode } from 'react';
import {
  buildAccessoryOptionGroups,
  buildIncludeRecommendedChanges,
  buildRadioSelectionChanges
} from 'utils/accessory-option-groups';
import { usesQuantitySelection } from 'utils/accessory-type-presentation';
import { isRecommendedRecSelected } from 'utils/include-recommended-accessories';
import AccessoryDrawer from './accessory-drawer';
import AccessoryOptionGroup from './accessory-option-group';

type SystemAccessoryType = {
  code: string;
  label: string;
  priority: number;
  limitOnePerSystem: boolean;
  description: string;
};

export type LineSetSettings = {
  msWireIncluded: boolean;
  zipTiesIncluded: boolean;
  wallSleeveIncluded: boolean;
  sealingClayIncluded: boolean;
  drainTubingIncluded: boolean;
  insulationWrapIncluded: boolean;
};

export type AccessoryRecommendation = {
  systemUniqueId: string;
  productType: string;
  productId: number;
  uniqueId: string;
  accessoryProductId: number;
  accessoryType: string;
  recommendedPriority: number;
  recommendedQuantity?: number;
  lineSetSettings?: LineSetSettings;
};

export type EquipmentAccessoryData = {
  equipmentUniqueId: string;
  equipmentName: string;
  condenserUniqueId: string;
  recommendations: AccessoryRecommendation[];
  selectedAccessories: Array<{
    productId: number;
    quantity: number;
    type: string;
  }>;
  accessoryProducts: Map<number, AccessoryProductType & { shortName?: string }>;
};

type AccessoryTypePanelProps = {
  accessoryType: SystemAccessoryType;
  equipment: EquipmentAccessoryData[];
  onAccessoryChange: (
    condenserUniqueId: string,
    accessory: AccessoryProductType,
    quantity: number,
    equipmentUniqueId?: string
  ) => void;
  onBatchAccessoryChange: (
    condenserUniqueId: string,
    changes: Array<{ accessory: AccessoryProductType; quantity: number }>,
    equipmentUniqueId?: string
  ) => void;
  onIncludeRecommendedToggle: (
    accessoryTypeCode: string,
    includeRecommended: boolean,
    recommendedAccessories: Array<{
      condenserUniqueId: string;
      equipmentUniqueId: string;
      accessory: AccessoryProductType;
      quantity: number;
    }>
  ) => void;
  isCompleted: boolean;
  includedAccessoryTypes?: Map<string, Set<string>>;
  equipmentHeaderExtras?: Map<string, ReactNode>;
};

export default function AccessoryTypePanel({
  accessoryType,
  equipment,
  onAccessoryChange,
  onBatchAccessoryChange,
  onIncludeRecommendedToggle,
  isCompleted,
  includedAccessoryTypes,
  equipmentHeaderExtras
}: AccessoryTypePanelProps) {
  const isAnyTypeIncludedForEquipment = (item: EquipmentAccessoryData) => {
    const set = includedAccessoryTypes?.get(item.equipmentUniqueId);
    if (!set || set.size === 0) return false;
    return Array.from(item.accessoryProducts.values()).some((product) =>
      set.has(product.type)
    );
  };

  const hasIncludedAccessories = equipment.some((item) =>
    isAnyTypeIncludedForEquipment(item)
  );

  const hasRecommendedAccessories =
    equipment.some((item) =>
      item.recommendations.some(
        (recommendation) =>
          recommendation.recommendedPriority === 1 &&
          item.accessoryProducts.has(recommendation.accessoryProductId)
      )
    ) || hasIncludedAccessories;

  const allRecommendedSelected =
    hasRecommendedAccessories &&
    equipment.every((item) =>
      item.recommendations
        .filter((recommendation) => recommendation.recommendedPriority === 1)
        .every((recommendation) =>
          isRecommendedRecSelected({
            item,
            rec: recommendation,
            accessoryTypeCode: accessoryType.code,
            includedTypes: includedAccessoryTypes?.get(item.equipmentUniqueId),
            airHandlerAccessories: {}
          })
        )
    );

  const getAddedCount = (item: EquipmentAccessoryData) =>
    item.recommendations.filter((recommendation) => {
      const quantity =
        item.selectedAccessories.find(
          (selected) => selected.productId === recommendation.accessoryProductId
        )?.quantity ?? 0;
      return quantity >= 1;
    }).length;

  const equipmentWithAccessories = equipment.filter(
    (item) => item.recommendations.length > 0
  );

  if (equipmentWithAccessories.length === 0) {
    return null;
  }

  const totalAddedCount = equipmentWithAccessories.reduce(
    (sum, item) => sum + getAddedCount(item),
    0
  );

  const quantitySelection = usesQuantitySelection(accessoryType.code);
  const optional = !hasRecommendedAccessories;

  const handleIncludeRecommended = (include: boolean) => {
    onIncludeRecommendedToggle(
      accessoryType.code,
      include,
      buildIncludeRecommendedChanges({
        equipment,
        include,
        includedAccessoryTypes,
        accessoryTypeCode: accessoryType.code,
        quantitySelection
      })
    );
  };

  return (
    <AccessoryDrawer
      title={accessoryType.label}
      subtitle={
        totalAddedCount > 0
          ? `(${totalAddedCount} accessor${
              totalAddedCount > 1 ? 'ies' : 'y'
            } added)`
          : undefined
      }
      description={accessoryType.description}
      required={!optional}
      hideSwitch={optional}
      defaultOpen={!optional}
      itemSelected={isCompleted}
      notNeeded={!allRecommendedSelected}
      handleNotNeeded={(notNeeded) => handleIncludeRecommended(!notNeeded)}
    >
      <div className='flex w-full flex-wrap'>
        {equipmentWithAccessories.map((item) => {
          const groups = buildAccessoryOptionGroups(item, {
            quantitySelection
          });
          const showHeadings = groups.length > 1;

          return (
            <div
              key={item.equipmentUniqueId}
              className='flex w-full flex-col gap-1 p-5'
            >
              <div className='mb-2 flex flex-wrap items-center justify-between gap-2'>
                <span className='text-base font-semibold text-gray-700'>
                  {item.equipmentName}
                </span>
                {equipmentHeaderExtras?.get(item.equipmentUniqueId)}
              </div>
              {isAnyTypeIncludedForEquipment(item) && (
                <div className='flex items-center rounded border border-dashed border-gray-300 p-5'>
                  <ControlHitArea disabled>
                    <input
                      type='checkbox'
                      checked
                      disabled
                      className='mr-4 h-5 w-5 cursor-not-allowed rounded border-gray-300 text-blue-600'
                    />
                  </ControlHitArea>
                  <span className='text-sm font-semibold text-blue-500'>
                    Includes drain tube packaged with line set
                  </span>
                </div>
              )}
              <div
                className={
                  quantitySelection
                    ? 'flex flex-col divide-y divide-gray-100'
                    : 'grid grid-cols-2 gap-3'
                }
              >
                {groups.map((group) => (
                  <AccessoryOptionGroup
                    key={group.type}
                    group={group}
                    quantitySelection={quantitySelection}
                    showHeading={showHeadings}
                    radioGroupName={`${accessoryType.code}-${item.equipmentUniqueId}-${group.type}`}
                    onCheckboxChange={(option, quantity) =>
                      onAccessoryChange(
                        item.condenserUniqueId,
                        option.product,
                        quantity,
                        item.equipmentUniqueId
                      )
                    }
                    onRadioSelect={(productId) => {
                      const changes = buildRadioSelectionChanges(
                        group,
                        productId
                      );
                      if (changes.length === 0) {
                        return;
                      }
                      onBatchAccessoryChange(
                        item.condenserUniqueId,
                        changes,
                        item.equipmentUniqueId
                      );
                    }}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </AccessoryDrawer>
  );
}
